import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { push, CallHistoryMethodAction } from 'connected-react-router';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

// Utils
import { titleSection } from '@webapp/utils/validators/release';
import scrollOnError from '@webapp/utils/release/scroll-on-error';
import { validateRelease } from '@webapp/utils/validators/release';

// Services
import { Price } from '@common/types/price/price';
import { Product } from '@services/product/types/product';

// Types
import { Release } from '@common/types/release/release';
import { AssetFile } from '@common/types/data/asset-file';
import SimpleTrack from '@common/types/release/simple-track';
import SimpleArtist from '@common/types/artist/simple-artist';
import { ReleaseArtist } from '@common/types/release/release-artist';
import { SimpleRelease } from '@common/types/release/simple-release';

// Store
import AppState from '@webapp/store/state';
import { plans } from '@webapp/store/products/selectors';
import { trackList } from '@webapp/store/tracks/selectors';
import { CreateOrder } from '@webapp/store/orders/action-creators';
import { CreateRelease } from '@webapp/store/release/action-creators';
import { UpdateTrackField } from '@webapp/store/tracks/action-creators';
import {
  UpdateField,
  UpdateReleaseStoreError,
} from '@webapp/store/release/action-creators';

// Components
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import TitleCtrl from '@webapp/controllers/release/title-input';
import CounterFixedButton from '@components/actions/counter-fixed-button';
import SummaryButton from '@components/actions/summary-button';
import { ErrorMessage } from '@components/basics/error-message';

// Controllers
import AnimatedHintCtrl from '@webapp/controllers/animated-hint';
import UploadSongCtrl from '@webapp/controllers/upload/upload-song/index';
import CleanTitleHintCtrl from '@webapp/controllers/release/clean-title-hint';
import UploadArtworkCtrl from '@webapp/controllers/upload/upload-artwork/index';

interface ReleaseInfoTitlePageProps {
  className?: string;
  release?: Partial<Release>;
  stepValid: boolean;
  activeArtist?: SimpleArtist;
  hasTracks: boolean;
  isMobile: boolean;
  isTablet: boolean;
  tracks: Partial<SimpleTrack>[];
  isReleaseValid: boolean;
  updateCover: (value?: AssetFile) => UpdateField<'cover'>;
  createRelease: (release: Partial<SimpleRelease>) => CreateRelease;
  nextSection: () => CallHistoryMethodAction;
  updateArtist: (artists: ReleaseArtist[]) => UpdateField<'artists'>;
  updateTrack: (trackId: string, value: string) => UpdateTrackField<'title'>;
  sendAnalytics: (
    error: string,
    field: string,
    value?: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
  goToPayment: (clientId: string, id: string) => CallHistoryMethodAction;
  goToSummary: (clientId: string, id: string) => CallHistoryMethodAction;
  product?: Product;
  selectPlan: (product: Product, price: Price) => void;
  updateTitle: (value: string) => UpdateField<'title'>;
  bottomMenuOpened: boolean;
}

const mapStateToProps = (state: AppState) => {
  const tracks = trackList(state.tracks.tracks, state.release.release?.id);
  const products = plans(state.products.list, false);
  return {
    product: products.find((p) => p.name === 'Distribution Pro'),
    release: state.release.release,
    stepValid: titleSection(state),
    activeArtist: state.artist.active,
    isMobile: state.ui.mobile,
    hasTracks: !!tracks.length,
    tracks: tracks,
    isReleaseValid: validateRelease(state),
    isTablet: state.ui.tablet,
    bottomMenuOpened: state.ui.bottomMenuOpened,
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch
): Pick<
  ReleaseInfoTitlePageProps,
  | 'updateCover'
  | 'createRelease'
  | 'nextSection'
  | 'updateArtist'
  | 'updateTrack'
  | 'sendAnalytics'
  | 'goToPayment'
  | 'goToSummary'
  | 'selectPlan'
  | 'updateTitle'
> => ({
  goToPayment: (clientId: string, id: string) =>
    dispatch(
      push(
        `/payment/${clientId}/${id || ''}?redirectTo=${
          window.location.pathname
        }`
      )
    ),
  goToSummary: (clientId: string, id: string) =>
    dispatch(push(`/release/${clientId}/${id || ''}/preview`)),
  selectPlan: (product: Product, price: Price) =>
    dispatch(
      new CreateOrder({
        product,
        price,
        quantity: 1,
      })
    ),
  updateCover: (value?: AssetFile) =>
    dispatch(new UpdateField<'cover'>('cover', value)),
  createRelease: (release: Partial<SimpleRelease>) =>
    dispatch(new CreateRelease(release)),
  nextSection: () => dispatch(push('./details')),
  updateArtist: (artists: ReleaseArtist[]) =>
    dispatch(new UpdateField<'artists'>('artists', artists)),
  updateTrack: (trackId: string, value: string) =>
    dispatch(new UpdateTrackField(trackId, 'title', value)),
  sendAnalytics: (
    error: string,
    field: string,
    value?: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(error, field, value, releaseId)),
  updateTitle: (value: string) =>
    dispatch(new UpdateField<'title'>('title', value)),
});

export const ReleaseInfoTitlePage = (props: ReleaseInfoTitlePageProps) => {
  const {
    release,
    tracks,
    activeArtist,
    updateArtist,
    isTablet,
    bottomMenuOpened,
  } = props;

  const hasArtist = !!(
    release?.artists?.length && release.artists.find((a) => a.role === 'owner')
  );

  const [trackError, setTrackError] = React.useState('');
  const [artworkError, setArtworkError] = React.useState('');
  const [trackingId, setTrackingId] = React.useState<string>();
  const [fileUploaded, setFileUploaded] = React.useState<boolean>(false);
  const [validateErrors, setValidateErrors] = React.useState(0);

  const match = useRouteMatch<{
    artistId: string;
    releaseId: string;
  }>();
  const { artistId, releaseId } = match.params;
  React.useEffect(() => {
    scrollOnError();
  }, [validateErrors]);

  React.useEffect(() => {
    if (!!release?.source && !release?.cover?.size) {
      setArtworkError('RELEASE.INVALID_SIZE');
    }
    if (!!release?.source) {
      setTrackError('');
    }
  }, [trackError, release, tracks]);

  const handleUpgrade = () => {
    if (props.product) {
      const price = props.product?.prices.find((p) => p.frequency === 'year');

      if (!price) {
        return;
      }
      handleSelection(props.product, price);
    }
  };

  const handleSelection = (product: Product, price: Price) => {
    props.selectPlan(product, price);
    return props.goToPayment(artistId ?? activeArtist?.id, releaseId);
  };
  const handleNextStepClick = () => {
    if (!hasArtist) {
      return false;
    }
    if (!isValid()) {
      setValidateErrors(validateErrors + 1);
      return false;
    }
    if (!release?.id) {
      props.createRelease(release || {});
    }
    props.nextSection();
  };

  const isValid = () => {
    let valid = true;
    const validations: Array<{
      field: string;
      fieldValue?: string;
      errorId: string;
    }> = [];

    if (!props.hasTracks) {
      setTrackError('RELEASE.MISSING_TRACKS');
      valid = valid && false;
    }

    if (!release?.cover?.url) {
      setArtworkError('RELEASE.MISSING_COVER');
      validations.push({
        field: 'Release Cover',
        fieldValue: release?.cover?.url,
        errorId: 'RELEASE.MISSING_COVER',
      });
      valid = valid && false;
    } else if (!release?.cover?.size) {
      setArtworkError('RELEASE.INVALID_SIZE');
      validations.push({
        field: 'Release Cover',
        fieldValue: release?.cover?.size.toString(),
        errorId: 'RELEASE.INVALID_SIZE',
      });
      valid = valid && false;
    } else {
      setArtworkError('');
      valid = valid && true;
    }

    if (!release?.title) {
      validations.push({
        field: 'Release Name',
        fieldValue: release?.title,
        errorId: 'RELEASE.MISSING_TITLE',
      });

      valid = valid && false;
    } else {
      valid = valid && true;
    }

    if (!valid && validations) {
      validations.forEach((validation) => {
        props.sendAnalytics(
          validation.errorId,
          validation.field,
          validation.fieldValue
        );
      });
    }

    return props.stepValid && valid;
  };

  React.useEffect(() => {
    if (!hasArtist && activeArtist && release) {
      const arr = release.artists || [];

      updateArtist(
        arr.concat([
          {
            ...activeArtist,
            role: 'owner',
          },
        ])
      );
    }
  }, [release, hasArtist, activeArtist, updateArtist]);

  return (
    <MotionOpacity className={props.className}>
      {!props.isMobile && (
        <Typography
          className="title mb-30"
          type="subheader"
          customFont="semi-bold"
        >
          <Translate id="RELEASE.SIDEBAR.LIST.COVER_MUSIC" />
        </Typography>
      )}

      <Wrapper>
        <TitleCtrl
          coverUrl={release?.cover?.url}
          handleUpgrade={handleUpgrade}
        />

        <CleanTitleHintCtrl title={release?.title || ''} noBorderBottom />

        <UploadArtworkCtrl
          id={'uploadMusicBtn'}
          hasError={
            <ErrorMessage
              className="mt-10"
              hasBackground
              errorId={artworkError ? artworkError : undefined}
            />
          }
          onChange={(url) => {
            props.updateCover(url);
            setFileUploaded(true);
            setArtworkError('');
          }}
          imgUrl={release?.cover?.url}
          onError={setArtworkError}
        />

        <UploadSongCtrl
          hasError={
            <ErrorMessage
              className="mt-10"
              hasBackground
              errorId={trackError}
            />
          }
          id={'uploadTrackBtn'}
          releaseId={release?.id}
          trackingId={trackingId}
          onTrackingId={setTrackingId}
          hasTrackList={true}
        />

        {fileUploaded && (
          <AnimatedHintCtrl noTitle align="left">
            <Translate id="RELEASE.NEXT_STEP_HINT" />
          </AnimatedHintCtrl>
        )}
      </Wrapper>

      {!bottomMenuOpened && (
        <CounterFixedButton
          id={'nextReleaseTitleBtn'}
          className={!isTablet ? '' : 'mt-30'}
          fixed={!isTablet}
          bottomMargin={props.isReleaseValid ? '55px' : undefined}
          isValid={release?.title && release?.cover?.url && props.hasTracks}
          currentStep={1}
          maxStep={3}
          customMaxStep={3}
          onNext={handleNextStepClick}
        />
      )}

      {props.isReleaseValid && (
        <SummaryButton
          className={!isTablet ? '' : 'mt-30'}
          fixed={!isTablet}
          onClick={() =>
            props.goToSummary(artistId ?? activeArtist?.id, releaseId)
          }
        />
      )}
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ReleaseInfoTitlePage)`
  .title {
    display: none;

    @media ${({ theme }) => theme.device.tablet} {
      display: flex;
    }
  }
`);

const Wrapper = styled.div`
  display: grid;
  grid-gap: 3px;
  padding-bottom: 100px;
  @media ${({ theme }) => theme.device.tablet} {
    padding-bottom: 0;
  }
`;
